import { Alert, alpha, useTheme } from "@mui/material";
import { Button } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

export type InvitationNotice = "template_changed" | "unavailable" | "target_year_not_after_diagnostic";
export type EstimateErrorReason = "throttled" | "outage";

export const useWarningAlertSx = () => {
    const theme = useTheme();

    return {
        border: 1,
        borderColor: "warning.main",
        borderRadius: 1,
        backgroundColor: alpha(theme.palette.warning.main, 0.04),
    };
};

export const InvitationNoticeAlert = (props: { notice: InvitationNotice }) => {
    const { notice } = props;
    const { t } = useTranslation();
    const sx = useWarningAlertSx();

    return (
        <Alert severity="warning" role="alert" sx={sx}>
            {notice === "template_changed" ?
                t(
                    "action_plan.invitation.template_changed",
                    "The proposed plan has just been updated. Review the new estimate, then confirm again.",
                )
            : notice === "unavailable" ?
                t(
                    "action_plan.invitation.proposal_unavailable",
                    "The proposed plan is no longer available. You can still create your own plan.",
                )
            :   t(
                    "action_plan.invitation.target_year_not_after_diagnostic",
                    "The proposed plan's target year must be later than this diagnostic's year. Create your own plan instead.",
                )
            }
        </Alert>
    );
};

export const EstimateErrorAlert = (props: { reason?: EstimateErrorReason; onRetry?: () => void }) => {
    const { reason, onRetry } = props;
    const { t } = useTranslation();
    const sx = useWarningAlertSx();

    return (
        <Alert severity="warning" role="status" sx={sx}>
            {reason === "throttled" ?
                t(
                    "action_plan.invitation.estimate_throttled",
                    "Too many requests. Wait a moment before requesting the proposed plan again, or create your own plan.",
                )
            : reason === "outage" ?
                t(
                    "action_plan.invitation.estimate_outage",
                    "The proposed plan can't be prepared right now. Try again in a moment, or create your own plan.",
                )
            :   t(
                    "action_plan.invitation.estimate_unavailable",
                    "We can't show the proposed plan right now. Try again in a moment, or create your own plan.",
                )
            }
            {onRetry && (
                <Button variant="text" onClick={onRetry}>
                    {t("action_plan.invitation.retry_estimate", "Try again")}
                </Button>
            )}
        </Alert>
    );
};
